import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  Fab,
  CircularProgress,
  ListItemAvatar,
  Badge,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { isLoggedIn } from "../session";
import ContactSearch from "./ContactSearch";

function Chats() {
  const [directChats, setDirectChats] = useState([]);
  const [authUserId, setAuthUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [contactSearchOpen, setContactSearchOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAuthUserId = async () => {
      const user = await isLoggedIn();
      if (user) setAuthUserId(user.uid);
    };
    fetchAuthUserId();
  }, []);

  useEffect(() => {
    if (!authUserId) return;
    setLoading(true);

    const chatsRef = collection(db, "directChats");
    const q = query(
      chatsRef,
      where("participants", "array-contains", authUserId),
      orderBy("lastMessageAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const chatsData = await Promise.all(
          snapshot.docs.map(async (chatDoc) => {
            const data = chatDoc.data();
            const otherId = data.participants.find((p) => p !== authUserId);
            let otherUser = { displayName: "Usuario", photoURL: "" };
            try {
              const userSnap = await getDoc(doc(db, "users", otherId));
              if (userSnap.exists()) otherUser = userSnap.data();
            } catch (_) {}
            return {
              id: chatDoc.id,
              otherUserId: otherId,
              otherUserName: otherUser.displayName || "Usuario",
              otherUserPhoto: otherUser.photoURL || "",
              lastMessage: data.lastMessage || "",
              lastMessageAt: data.lastMessageAt,
              unread: data.unreadCount?.[authUserId] || 0,
            };
          })
        );
        setDirectChats(chatsData);
        setLoading(false);
      },
      (err) => {
        console.warn("Chats:", err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authUserId]);

  const handleChatClick = (chatId, otherUserId) => {
    navigate(`/direct-chat/${chatId}/${otherUserId}/${authUserId}`);
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Ahora";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000)
      return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  };

  return (
    <Box sx={{ mt: 16, mb: 10, position: "relative" }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : directChats.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            mt: 10,
            gap: 2,
          }}
        >
          <EditIcon sx={{ fontSize: 64, opacity: 0.15 }} />
          <Typography variant="body1" color="text.secondary">
            No tenés conversaciones todavía.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Presioná ✏️ para buscar contactos e iniciar un chat.
          </Typography>
        </Box>
      ) : (
        <List disablePadding>
          {directChats.map((chat, i) => (
            <React.Fragment key={chat.id}>
              <ListItem
                alignItems="flex-start"
                onClick={() => handleChatClick(chat.id, chat.otherUserId)}
                sx={{
                  cursor: "pointer",
                  px: 2,
                  py: 1.5,
                  "&:hover": { bgcolor: "#f5f7ff" },
                  bgcolor: chat.unread > 0 ? "#f0f4ff" : "transparent",
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={chat.unread > 0 ? chat.unread : null}
                    color="primary"
                    overlap="circular"
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  >
                    <Avatar
                      src={chat.otherUserPhoto}
                      alt={chat.otherUserName}
                      sx={{ border: "2px solid #1976d2", width: 48, height: 48 }}
                    >
                      {(chat.otherUserName || "?")[0].toUpperCase()}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography
                      variant="body1"
                      fontWeight={chat.unread > 0 ? "bold" : "normal"}
                      noWrap
                    >
                      {chat.otherUserName}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ fontWeight: chat.unread > 0 ? "bold" : "normal" }}
                    >
                      {chat.lastMessage || "Iniciar conversación"}
                    </Typography>
                  }
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1, mt: 0.5, whiteSpace: "nowrap" }}
                >
                  {formatTime(chat.lastMessageAt)}
                </Typography>
              </ListItem>
              {i < directChats.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      )}

      {/* FAB nueva conversación */}
      <Fab
        color="primary"
        aria-label="nueva conversación"
        sx={{ position: "fixed", bottom: 72, right: 20, zIndex: 900 }}
        onClick={() => setContactSearchOpen(true)}
      >
        <EditIcon />
      </Fab>

      <ContactSearch
        open={contactSearchOpen}
        onClose={() => setContactSearchOpen(false)}
        authUserId={authUserId}
      />
    </Box>
  );
}

export default Chats;
